import React, { useState, useEffect, useRef } from 'react';
import { X, ArrowRight, ArrowLeft, Wand2, Package, Sparkles, Palette, Image as ImageIcon, Check } from 'lucide-react';

const FashDeckStudioTour = ({ onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [highlightedElement, setHighlightedElement] = useState(null);

  const tourSteps = [
    {
      id: 'welcome',
      title: 'Welcome to FashDeck Studio! 🎨',
      description: 'FashDeck Studio is your creative workspace for editing and enhancing your AI-generated designs. Let\'s take a quick tour to get you started.',
      position: 'center',
      showHighlight: false,
    },
    {
      id: 'import',
      title: 'Import Your Designs',
      description: 'Click the "Import Kit" button in the header to bring in any of your existing AI-generated kits. You can then edit, enhance, and create variations.',
      position: 'center',
      elementSelector: '[data-tour="import-button"]',
      showHighlight: true,
    },
    {
      id: 'design-editor',
      title: 'Design Editor',
      description: 'Once you import a kit, use the Design Editor for necklines, sleeves, silhouettes, length, and fabric color swatches; Multi-Fabric and Embellishments add more edits.',
      position: 'center',
      elementSelector: '[data-tour="design-editor"]',
      showHighlight: true,
    },
    {
      id: 'magic-edit',
      title: 'Magic Edit Feature',
      description: 'The Magic Edit feature allows you to make custom edits using text prompts. Describe what you want to change, and AI will transform your design instantly.',
      position: 'center',
      showHighlight: false,
    },
    {
      id: 'complete',
      title: 'You\'re All Set! ✨',
      description: 'You now know the basics of FashDeck Studio. Start by importing a kit and exploring the editing features. Happy designing!',
      position: 'center',
      showHighlight: false,
    },
  ];

  useEffect(() => {
    const step = tourSteps[currentStep];
    if (step && step.elementSelector) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        const element = document.querySelector(step.elementSelector);
        if (element) {
          setHighlightedElement(element);
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          setHighlightedElement(null);
        }
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setHighlightedElement(null);
    }
  }, [currentStep]);

  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    localStorage.setItem('fashdeck-studio-tour-completed', 'true');
    if (onComplete) onComplete();
    if (onClose) onClose();
  };

  const handleSkip = () => {
    handleComplete();
  };

  const currentStepData = tourSteps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === tourSteps.length - 1;

  // Calculate tooltip position
  const getTooltipPosition = () => {
    if (currentStepData.position === 'center') {
      return 'fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2';
    }
    if (highlightedElement) {
      const rect = highlightedElement.getBoundingClientRect();
      if (currentStepData.position === 'top-right') {
        return `fixed top-[${rect.top - 20}px] left-[${rect.right + 20}px]`;
      }
      return 'fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2';
    }
    return 'fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2';
  };

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 z-[9998] bg-black/60 backdrop-blur-sm">
        {/* Highlight overlay with cutout */}
        {highlightedElement && currentStepData.showHighlight && (
          <div
            className="absolute border-4 border-purple-500 rounded-lg shadow-2xl pointer-events-none"
            style={{
              top: highlightedElement.getBoundingClientRect().top - 8,
              left: highlightedElement.getBoundingClientRect().left - 8,
              width: highlightedElement.getBoundingClientRect().width + 16,
              height: highlightedElement.getBoundingClientRect().height + 16,
              boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
            }}
          />
        )}
      </div>

      {/* Tooltip */}
      <div
        className={`fixed z-[9999] ${currentStepData.position === 'center' ? 'top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2' : highlightedElement ? `top-[${highlightedElement.getBoundingClientRect().bottom + 20}px] left-1/2 transform -translate-x-1/2` : 'top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2'} max-w-md w-[90%] sm:w-full`}
      >
        <div className="bg-white rounded-2xl shadow-2xl border-2 border-purple-200 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-pink-600 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <Wand2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">FashDeck Studio Tour</h3>
                <p className="text-xs text-purple-100">
                  Step {currentStep + 1} of {tourSteps.length}
                </p>
              </div>
            </div>
            <button
              onClick={handleSkip}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              title="Skip tour"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            <h4 className="text-xl font-bold text-gray-800 mb-3">{currentStepData.title}</h4>
            <p className="text-gray-600 mb-6 leading-relaxed">{currentStepData.description}</p>

            {/* Progress dots */}
            <div className="flex items-center justify-center gap-2 mb-6">
              {tourSteps.map((_, index) => (
                <div
                  key={index}
                  className={`h-2 rounded-full transition-all ${
                    index === currentStep
                      ? 'w-8 bg-gradient-to-r from-purple-500 to-pink-500'
                      : index < currentStep
                      ? 'w-2 bg-purple-300'
                      : 'w-2 bg-gray-300'
                  }`}
                />
              ))}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between gap-3">
              <button
                onClick={handlePrevious}
                disabled={isFirstStep}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  isFirstStep
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <ArrowLeft className="w-4 h-4" />
                Previous
              </button>

              {isLastStep ? (
                <button
                  onClick={handleComplete}
                  className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all shadow-md"
                >
                  <Check className="w-4 h-4" />
                  Get Started
                </button>
              ) : (
                <button
                  onClick={handleNext}
                  className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all shadow-md"
                >
                  Next
                  <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FashDeckStudioTour;

